/** Approve a pending lifecycle request (tenant retirement CONTRACT §8.3): a reason and the tenant's
 *  code typed again. A close, or a reopen of a closed tenant, applies at once (200, `applied`); a
 *  release or a reopen of a released tenant starts a job (202, `applying`; the Lifecycle tab
 *  follows its steps).
 *
 *  A release first loads its dry run (`GET …/release_preview/`: the placeholder code, the ids it
 *  re-keys, the state it purges, the keys it revokes) and what blocks it now — judged with the
 *  request's recorded override, read afresh each time the dialog opens. Approve stays off while
 *  it is being read and while it lists blockers. A preview that cannot be read says why; Approve
 *  then needs the explicit "approve without the preview" (the backend checks the release's
 *  conditions again either way).
 *
 *  With two-person approval on, the requester cannot approve their own request: for them the
 *  dialog says so and Approve stays off. The backend refuses it anyway (403 `self_approval`),
 *  which is put in words like every other refusal. A request decided meanwhile (409
 *  `request_not_pending`) turns Approve off and re-reads the tab. */
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  LIFECYCLE_REASON_MAX,
  lifecycleApi,
  lifecycleError,
  lifecycleErrorWords,
  lifecycleKeys,
  type TenantLifecycle,
} from '@/api/tenantLifecycle';
import type { TenantRequest } from '@/api/tenantRequests';
import { useAuth } from '@/auth/AuthProvider';
import { AppDialog } from '@/components/AppDialog';
import { useSnackbar } from '@/components/Snackbar';

import { LifecycleRequestQuote } from './lifecycleFormat';
import { ReleasePreviewPanel } from './ReleasePreviewPanel';
import { confirmSchema } from './RequestLifecycleDialog';
import { useLifecycleRefresh } from './useLifecycleRefresh';

const WHAT: Record<TenantRequest['kind'], string> = {
  go_live: '',
  close:
    'Approving closes the tenant now: its users are signed out and refused, its API keys and MCP access stop working, webhooks are ignored, sends and tasks are dropped, and its sandbox is suspended. Its data stays.',
  release:
    'Approving starts the job that gives up the tenant’s code; the tenant is releasing until it has finished, and the Lifecycle tab follows its steps.',
  reopen:
    'Approving reopens the tenant now: its users sign in again, API keys, webhooks and sends work again, and its sandbox resumes.',
};

const APPROVE_LABEL: Record<TenantRequest['kind'], string> = {
  go_live: 'Approve',
  close: 'Approve and close',
  release: 'Approve and release',
  reopen: 'Approve and reopen',
};

/** Two-person approval is on and `userId` asked for the request. */
export function isOwnRequest(
  request: Pick<TenantRequest, 'requested_by'>,
  userId: number | null | undefined,
): boolean {
  return userId != null && request.requested_by?.id === userId;
}

interface Props {
  topId: number;
  code: string;
  /** The request to approve; null keeps the dialog closed. */
  request: TenantRequest | null;
  twoPersonRequired: boolean;
  /** The tenant's lifecycle now: a reopen of a released tenant runs a job first. */
  lifecycle?: TenantLifecycle;
  onClose: () => void;
}

/** The confirmation, plus a release's explicit approval without its (unreadable) preview. */
function approveSchema(code: string) {
  return confirmSchema(code).extend({ without_preview: z.boolean() });
}
type ApproveValues = z.infer<ReturnType<typeof approveSchema>>;
const EMPTY: ApproveValues = { reason: '', confirm_code: '', without_preview: false };

const REOPEN_RELEASED =
  'Approving starts the job that gives the tenant its code back and then reopens it; the Lifecycle tab follows its steps.';

export function ApproveLifecycleDialog({
  topId,
  code,
  request,
  twoPersonRequired,
  lifecycle,
  onClose,
}: Props) {
  const snackbar = useSnackbar();
  const refresh = useLifecycleRefresh();
  const { user } = useAuth();
  const schema = useMemo(() => approveSchema(code), [code]);
  const { control, register, handleSubmit, reset, setError, watch, formState } = useForm<ApproveValues>({
    resolver: zodResolver(schema),
    defaultValues: EMPTY,
  });

  const mut = useMutation({
    mutationFn: ({ id, ...v }: ApproveValues & { id: number }) =>
      lifecycleApi.approve(topId, id, { reason: v.reason.trim(), confirm_code: v.confirm_code }),
    onSuccess: (r) => {
      snackbar.show(
        r.status === 'applying'
          ? 'Approved — the job is running.'
          : r.kind === 'close'
            ? 'Approved — the tenant is closed.'
            : r.kind === 'reopen'
              ? 'Approved — the tenant is active again.'
              : 'Approved.',
        'success',
      );
      refresh();
      onClose();
    },
    onError: (e) => {
      const code = lifecycleError(e)?.code;
      if (code === 'confirm_code_mismatch') setError('confirm_code', { message: lifecycleErrorWords(e) });
      if (code === 'reason_required') setError('reason', { message: lifecycleErrorWords(e) });
      if (
        code === 'request_not_pending' ||
        code === 'not_active' ||
        code === 'not_closed' ||
        code === 'release_too_early' ||
        code === 'offboarding_incomplete' ||
        code === 'work_running' ||
        code === 'code_taken'
      )
        refresh();
    },
  });

  const { reset: resetMutation } = mut;
  useEffect(() => {
    if (!request) return;
    reset(EMPTY);
    resetMutation();
  }, [request, reset, resetMutation]);

  const errorCode = lifecycleError(mut.error)?.code;
  const showError =
    mut.error != null && errorCode !== 'confirm_code_mismatch' && errorCode !== 'reason_required';
  const ownRequest = twoPersonRequired && request != null && isOwnRequest(request, user?.id);
  // A release is approved knowing what it does: its preview, read now.
  const release = request?.kind === 'release' && !ownRequest;
  const preview = useQuery({
    queryKey: lifecycleKeys.releasePreview(topId),
    queryFn: () => lifecycleApi.releasePreview(topId),
    enabled: release,
    // Read afresh on every opening: a cached preview may predate a blocker.
    staleTime: 0,
    retry: false,
  });
  const withoutPreview = watch('without_preview');
  const previewHolds =
    release &&
    (preview.isFetching ||
      (preview.isError
        ? !withoutPreview
        : preview.data == null || preview.data.blockers.length > 0));
  // Another try would only meet the same refusal.
  const refused = ownRequest || errorCode === 'self_approval' || errorCode === 'request_not_pending';
  const blocked = refused || previewHolds;
  const submit = handleSubmit((v) => request && mut.mutate({ ...v, id: request.id }));
  const closing = request?.kind === 'close';

  return (
    <AppDialog
      open={request != null}
      onClose={onClose}
      title="Approve the request"
      size={release ? 'md' : 'sm'}
      disableBackdropClose={mut.isPending || formState.isDirty}
      actions={
        <>
          <Button onClick={onClose} disabled={mut.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={closing ? 'error' : 'primary'}
            disabled={mut.isPending || blocked}
            onClick={() => void submit()}
            startIcon={mut.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {request ? APPROVE_LABEL[request.kind] : 'Approve'}
          </Button>
        </>
      }
    >
      {request && (
        <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
          <LifecycleRequestQuote request={request} />
          {ownRequest ? (
            <Alert severity="info">
              Two-person approval is on and you asked for this request, so someone else has to
              approve it.
            </Alert>
          ) : (
            <>
              <Typography variant="body2">
                {request.kind === 'reopen' && lifecycle === 'released' ? REOPEN_RELEASED : WHAT[request.kind]}
              </Typography>
              {release && preview.isFetching && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} role="status">
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Reading what the release does…
                  </Typography>
                </Box>
              )}
              {release && preview.isError && !preview.isFetching && (
                <>
                  <Alert
                    severity="error"
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => void preview.refetch({ cancelRefetch: false })}
                      >
                        Try again
                      </Button>
                    }
                  >
                    {lifecycleErrorWords(preview.error, 'Couldn’t read what the release does.')}
                  </Alert>
                  <Controller
                    control={control}
                    name="without_preview"
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                            onBlur={field.onBlur}
                            inputRef={field.ref}
                            disabled={mut.isPending}
                          />
                        }
                        label="Approve without the preview — the release’s conditions are checked again when it is approved"
                      />
                    )}
                  />
                </>
              )}
              {release && preview.data && !preview.isError && (
                <ReleasePreviewPanel topId={topId} preview={preview.data} />
              )}
              <TextField
                {...register('reason')}
                label="Your reason"
                required
                multiline
                minRows={2}
                size="small"
                autoFocus
                disabled={mut.isPending || refused}
                inputProps={{ maxLength: LIFECYCLE_REASON_MAX }}
                error={Boolean(formState.errors.reason)}
                helperText={formState.errors.reason?.message ?? 'Kept as the decision note and in the audit record.'}
              />
              <TextField
                {...register('confirm_code')}
                label="Tenant code"
                required
                size="small"
                disabled={mut.isPending || refused}
                autoComplete="off"
                inputProps={{ spellCheck: false, maxLength: 64 }}
                error={Boolean(formState.errors.confirm_code)}
                helperText={formState.errors.confirm_code?.message ?? `Type ${code} to confirm.`}
              />
            </>
          )}
          {showError && <Alert severity="error">{lifecycleErrorWords(mut.error)}</Alert>}
        </Box>
      )}
    </AppDialog>
  );
}
